import React from 'react';
import { adaptSize } from '../../utils/remAdapter'

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange
}) => {
  // 生成分页器显示的条目
  const getPaginationItems = () => {
    const items: (number | string)[] = [];

    if (totalPages <= 5) {
      // 如果总页数少于5，直接显示全部页码
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      // 总页数较多时的逻辑
      if (currentPage <= 3) {
        // 当前页靠近开头：1 2 3 ... 100
        items.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // 当前页靠近结尾：1 ... 98 99 100
        items.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        // 当前页在中间：1 ... 49 50 51 ... 100
        // 保持7个元素：左右箭头 + 5个中间项
        items.push(1, '...', currentPage, '...', totalPages);
      }
    }

    return items;
  };

  const items = getPaginationItems();
  const getButtonStyle = (isActive: boolean, isEllipsis: boolean = false): React.CSSProperties => ({
    width: adaptSize('40px'),
    height: adaptSize('40px'),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px',
    background: isActive ? '#333333' : '#F4F4F8',
    color: isActive ? '#F4F4F8' : '#333333',
    border: 'none',
    cursor: isEllipsis ? 'default' : 'pointer',
    opacity: isEllipsis ? 0.6 : 1,
    fontFamily: 'PingFang SC, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: adaptSize('16px'),
    fontWeight: 'normal',
    lineHeight: 1,
    letterSpacing: '0px',
    fontVariationSettings: '"opsz" auto',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    outline: 'none'
  });

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0px',
    gap: adaptSize('8px'),
    width: '100%',
    boxSizing: 'border-box',
    marginTop: adaptSize('40px')
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div style={containerStyle}>
      {/* 左箭头 */}
      <button
        type="button"
        style={{
          ...getButtonStyle(false),
          color: currentPage === 1 ? 'rgba(51, 51, 51, .3)' : '#333',
          // opacity: currentPage === 1 ? 0.4 : 1,
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
        }}
        onClick={handlePrev}
        disabled={currentPage === 1}
        aria-label="上一页"
      >
        &lt;
      </button>

      {/* 中间页码 */}
      {items.map((item, index) => (
        <button
          key={index}
          type="button"
          style={getButtonStyle(item === currentPage, item === '...')}
          onClick={() => handlePageClick(item)}
          disabled={item === '...'}
          aria-label={typeof item === 'number' ? `第${item}页` : '省略'}
        >
          {item}
        </button>
      ))}

      {/* 右箭头 */}
      <button
        type="button"
        style={{
          ...getButtonStyle(false),
          color: currentPage === totalPages ? 'rgba(51, 51, 51, .3)' : '#333',
          // opacity: currentPage === totalPages ? 0.4 : 1,
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
        }}
        onClick={handleNext}
        disabled={currentPage === totalPages}
        aria-label="下一页"
      >
        &gt;
      </button>
    </div>
  );
};

export default Pagination;